import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { usePlatformAuth } from "../context/PlatformAuthContext";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  Button,
  Input,
  Label,
  Alert,
  AlertDescription,
} from "@homy/ui";
import {
  Lock,
  AlertCircle,
  ArrowRight,
  CheckCircle2,
  Eye,
  EyeOff,
} from "lucide-react";
import { HomyLogo } from "../components/brand/HomyLogo";

// Dead code elimination constant for platform console
const SHOW_DEV_BYPASS = !import.meta.env.PROD || import.meta.env.VITE_ENABLE_QA_LOGIN === "true";

export function LoginPage() {
  const { login } = usePlatformAuth();
  const navigate = useNavigate();

  // In production, do NOT leak prefilled platform email
  const [email, setEmail] = useState(SHOW_DEV_BYPASS ? "platform@homy.com" : "");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const formatErrorMessage = (err) => {
    const rawMsg = err?.message || "";
    const status = err?.status || err?.response?.status;

    // 401 Unauthorized handling: credential mismatch or missing password
    if (
      status === 401 ||
      rawMsg.toLowerCase().includes("credential") ||
      rawMsg.toLowerCase().includes("invalid operator") ||
      rawMsg.toLowerCase().includes("user not found") ||
      rawMsg.toLowerCase().includes("password is required") ||
      rawMsg.toLowerCase().includes("unauthorized")
    ) {
      return "Invalid email or password";
    }

    // 403 Forbidden handling: non-platform operator
    if (
      status === 403 ||
      rawMsg.toLowerCase().includes("platform admin") ||
      rawMsg.toLowerCase().includes("forbidden")
    ) {
      return "Access denied: Corporate platform administrator privileges required.";
    }

    // Origin/network/server errors (e.g. 502, 503, 520, connection drops)
    if (
      (status && status >= 500) ||
      rawMsg.toLowerCase().includes("cloudflare") ||
      rawMsg.toLowerCase().includes("origin") ||
      rawMsg.toLowerCase().includes("network error")
    ) {
      return "Platform service is temporarily unreachable or restarting. Please try again shortly.";
    }

    // General fallback
    return rawMsg || "Failed to authenticate operator. Please check your connection.";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      setError("Please enter your platform operator email");
      return;
    }

    // In production, password is strictly mandatory
    if (!SHOW_DEV_BYPASS && !password) {
      setError("Please enter your platform operator password");
      return;
    }

    setLoading(true);
    setError(null);
    try {
      await login({
        email: trimmedEmail,
        password: password || undefined,
      });
      navigate("/overview");
    } catch (err) {
      setError(formatErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleQuickLogin = SHOW_DEV_BYPASS
    ? async () => {
        setEmail("platform@homy.com");
        setPassword("");
        setLoading(true);
        setError(null);
        try {
          await login({
            email: "platform@homy.com",
            password: "",
          });
          navigate("/overview");
        } catch (err) {
          setError(formatErrorMessage(err));
        } finally {
          setLoading(false);
        }
      }
    : null;

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <div className="w-full max-w-md space-y-6">
        <HomyLogo
          variant="login"
          subtitle="Sovereign control plane for multi-tenant property management"
        />

        <Card className="border-border">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Lock className="h-4 w-4 text-primary" />
              Operator Authentication
            </CardTitle>
            <CardDescription className="text-xs">
              Sign in with your corporate platform administrator identity
            </CardDescription>
          </CardHeader>

          <CardContent className="space-y-4">
            {error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="operator-email">Platform Operator Email</Label>
                <Input
                  id="operator-email"
                  type="email"
                  placeholder="platform@homy.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={loading}
                  className="bg-input"
                  autoComplete="email"
                  autoFocus={!SHOW_DEV_BYPASS}
                />
              </div>

              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label htmlFor="operator-password">Platform Operator Password</Label>
                </div>
                <div className="relative">
                  <Input
                    id="operator-password"
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required={!SHOW_DEV_BYPASS}
                    disabled={loading}
                    className="bg-input pr-10"
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 h-8 w-8 flex items-center justify-center rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    tabIndex={-1}
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </div>

              <Button type="submit" className="w-full gap-2 min-h-[44px]" disabled={loading}>
                {loading ? "Authenticating Session..." : "Authenticate Operator"}
                <ArrowRight className="h-4 w-4" />
              </Button>
            </form>

            {SHOW_DEV_BYPASS && (
              <>
                <div className="relative my-4">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-border"></div>
                  </div>
                  <div className="relative flex justify-center text-[10px] uppercase">
                    <span className="bg-card px-2 text-muted-foreground">Local Dev Bypass</span>
                  </div>
                </div>

                <Button
                  type="button"
                  variant="outline"
                  className="w-full text-xs text-muted-foreground hover:text-foreground border-border gap-2 min-h-[44px]"
                  onClick={handleQuickLogin}
                  disabled={loading}
                >
                  <CheckCircle2 className="h-4 w-4 text-emerald-400" />
                  1-Click Sign In as platform@homy.com
                </Button>
              </>
            )}
          </CardContent>

        </Card>
      </div>
    </div>
  );
}

export default LoginPage;
